import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, FileText, Palette, Save, Wallet } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { AppLayout, PageHeader } from '../components/layout/AppLayout'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Card, CardBody } from '../components/ui/Card'
import { Skeleton } from '../components/ui/Empty'
import { Input, Label, Select, Textarea } from '../components/ui/Input'
import { apiClient } from '../lib/api'
import { cn } from '../lib/utils'

type FullConfig = {
  id: string
  version: number
  company: Record<string, string | number | string[] | undefined>
  fiscal: Record<string, string | number | boolean | undefined>
  pricing: {
    hourlyRate?: number
    hourlyRateOnsite?: number
    currency?: string
    paymentTermsDefault?: string
    warrantyMonths?: number
    maintenanceAnnualMin?: number
    vpsAnnual?: number
    services?: Array<{
      name: string
      description?: string
      priceFrom?: number
      priceTo?: number
      unit?: string
      notes?: string
    }>
    promo?: string
    notes?: string
  }
  branding: {
    emailSignatureHtml?: string
    primaryColor?: string
    secondaryColor?: string
    accentColor?: string
    logoUrl?: string
    tagline?: string
  }
}

type Tab = 'company' | 'fiscal' | 'pricing' | 'branding'

const TABS: { id: Tab; label: string; icon: typeof Building2 }[] = [
  { id: 'company', label: 'Anagrafica', icon: Building2 },
  { id: 'fiscal', label: 'Fiscale', icon: FileText },
  { id: 'pricing', label: 'Prezzi', icon: Wallet },
  { id: 'branding', label: 'Branding', icon: Palette },
]

export function TenantSettingsPage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState<Tab>('company')

  const { data, isLoading } = useQuery({
    queryKey: ['tenant-config'],
    queryFn: () => apiClient.get<FullConfig>('/tenant-config'),
  })

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiClient.patch('/tenant-config', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-config'] }),
  })

  return (
    <AppLayout>
      <PageHeader
        title="Impostazioni Tenant"
        description={data ? `Versione corrente: v${data.version}` : 'Configurazione del tuo tenant'}
        action={
          <Badge variant="info">{data?.company?.name ? String(data.company.name) : 'tenant'}</Badge>
        }
      />

      <div className="grid grid-cols-12 gap-6">
        <aside className="col-span-12 md:col-span-3">
          <nav className="space-y-1">
            {TABS.map((t) => {
              const Icon = t.icon
              const active = tab === t.id
              return (
                <button
                  type="button"
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={cn(
                    'w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm text-left transition',
                    active
                      ? 'bg-zinc-800/80 text-zinc-50'
                      : 'text-zinc-400 hover:bg-zinc-800/40 hover:text-zinc-200',
                  )}
                >
                  <Icon className="size-4" /> {t.label}
                </button>
              )
            })}
          </nav>
        </aside>

        <section className="col-span-12 md:col-span-9 space-y-4">
          {isLoading || !data ? (
            <Card>
              <CardBody>
                <Skeleton className="h-6 w-1/3 mb-3" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-2/3" />
              </CardBody>
            </Card>
          ) : tab === 'company' ? (
            <CompanyForm
              data={data.company}
              onSave={(patch) => save.mutate({ company: patch })}
              saving={save.isPending}
            />
          ) : tab === 'fiscal' ? (
            <FiscalForm
              data={data.fiscal}
              onSave={(patch) => save.mutate({ fiscal: patch })}
              saving={save.isPending}
            />
          ) : tab === 'pricing' ? (
            <PricingForm
              data={data.pricing}
              onSave={(patch) => save.mutate({ pricing: patch, replace: true })}
              saving={save.isPending}
            />
          ) : (
            <BrandingForm
              data={data.branding}
              onSave={(patch) => save.mutate({ branding: patch })}
              saving={save.isPending}
            />
          )}
          {save.isError && (
            <div className="text-sm text-red-400">
              {(save.error as Error)?.message ?? 'Errore salvataggio'}
            </div>
          )}
          {save.isSuccess && <div className="text-sm text-emerald-400">Salvato.</div>}
        </section>
      </div>
    </AppLayout>
  )
}

// ============================================================
// Forms
// ============================================================

function pickFormStrings(e: FormEvent<HTMLFormElement>) {
  const fd = new FormData(e.currentTarget)
  const obj: Record<string, string | number | undefined> = {}
  for (const [k, v] of fd.entries()) {
    const s = String(v).trim()
    obj[k] = s === '' ? undefined : s
  }
  return obj
}

function CompanyForm({
  data,
  onSave,
  saving,
}: {
  data: FullConfig['company']
  onSave: (patch: Record<string, unknown>) => void
  saving: boolean
}) {
  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const patch = pickFormStrings(e)
    if (patch.yearFounded) patch.yearFounded = Number(patch.yearFounded)
    if (patch.languages && typeof patch.languages === 'string') {
      // @ts-expect-error coerce
      patch.languages = patch.languages
        .split(',')
        .map((s: string) => s.trim())
        .filter(Boolean)
    }
    onSave(patch)
  }
  const def = (k: string) => (data?.[k] != null ? String(data[k] ?? '') : '')

  return (
    <Card>
      <CardBody>
        <form onSubmit={onSubmit} className="space-y-4">
          <h3 className="text-base font-semibold text-zinc-50">Anagrafica azienda</h3>
          <Grid>
            <Field label="Nome commerciale" name="name" defaultValue={def('name')} />
            <Field label="Ragione sociale" name="legalName" defaultValue={def('legalName')} />
            <Field label="Tagline" name="tagline" defaultValue={def('tagline')} colSpan={2} />
            <Field label="P.IVA" name="vatNumber" defaultValue={def('vatNumber')} />
            <Field label="Codice Fiscale" name="fiscalCode" defaultValue={def('fiscalCode')} />
            <Field label="REA" name="rea" defaultValue={def('rea')} />
            <Field label="Anno fondazione" name="yearFounded" defaultValue={def('yearFounded')} />
            <Field label="Owner" name="ownerName" defaultValue={def('ownerName')} />
            <Field label="Ruolo owner" name="ownerRole" defaultValue={def('ownerRole')} />
            <Field label="Indirizzo" name="address" defaultValue={def('address')} colSpan={2} />
            <Field label="Città" name="city" defaultValue={def('city')} />
            <Field label="Provincia" name="province" defaultValue={def('province')} />
            <Field label="CAP" name="postalCode" defaultValue={def('postalCode')} />
            <Field label="Country (ISO)" name="country" defaultValue={def('country') || 'IT'} />
            <Field label="Telefono" name="phone" defaultValue={def('phone')} />
            <Field label="Email" name="email" defaultValue={def('email')} />
            <Field label="PEC" name="pec" defaultValue={def('pec')} />
            <Field label="Sito" name="website" defaultValue={def('website')} />
            <Field label="IBAN" name="iban" defaultValue={def('iban')} colSpan={2} />
            <Field label="Banca" name="bank" defaultValue={def('bank')} colSpan={2} />
            <Field
              label="Orari"
              name="operatingHours"
              defaultValue={def('operatingHours')}
              colSpan={2}
            />
            <TextField
              label="Area operativa"
              name="serviceArea"
              defaultValue={def('serviceArea')}
              colSpan={2}
            />
            <Field
              label="Lingue (CSV)"
              name="languages"
              defaultValue={
                Array.isArray(data?.languages) ? (data.languages as string[]).join(', ') : ''
              }
              colSpan={2}
            />
          </Grid>
          <SaveButton saving={saving} />
        </form>
      </CardBody>
    </Card>
  )
}

function FiscalForm({
  data,
  onSave,
  saving,
}: {
  data: FullConfig['fiscal']
  onSave: (patch: Record<string, unknown>) => void
  saving: boolean
}) {
  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const patch: Record<string, unknown> = {}
    for (const [k, v] of fd.entries()) {
      const s = String(v).trim()
      if (s === '') {
        patch[k] = undefined
        continue
      }
      if (['vatRateDefault', 'withholdingTaxDefaultRate', 'stampDutyAmount'].includes(k)) {
        patch[k] = Number(s)
      } else if (['fatturaElettronicaEnabled', 'stampDutyEnabled'].includes(k)) {
        patch[k] = s === 'true'
      } else {
        patch[k] = s
      }
    }
    onSave(patch)
  }
  const def = (k: string) => (data?.[k] != null ? String(data[k] ?? '') : '')

  return (
    <Card>
      <CardBody>
        <form onSubmit={onSubmit} className="space-y-4">
          <h3 className="text-base font-semibold text-zinc-50">Dati fiscali</h3>
          <Grid>
            <div>
              <Label htmlFor="regime">Regime</Label>
              <Select id="regime" name="regime" defaultValue={def('regime') || 'ordinario'}>
                <option value="ordinario">Ordinario</option>
                <option value="forfettario">Forfettario</option>
                <option value="agevolato">Agevolato</option>
                <option value="reverse_charge">Reverse charge</option>
                <option value="esente">Esente</option>
                <option value="non_imponibile">Non imponibile</option>
              </Select>
            </div>
            <Field label="Codice ATECO" name="atecoCode" defaultValue={def('atecoCode')} />
            <Field label="Codice SDI" name="sdiCode" defaultValue={def('sdiCode')} />
            <Field
              label="Aliquota IVA default (%)"
              name="vatRateDefault"
              type="number"
              defaultValue={def('vatRateDefault')}
            />
            <Field
              label="Ritenuta d'acconto default (%)"
              name="withholdingTaxDefaultRate"
              type="number"
              defaultValue={def('withholdingTaxDefaultRate')}
            />
            <Field
              label="Bollo importo (€)"
              name="stampDutyAmount"
              type="number"
              defaultValue={def('stampDutyAmount')}
            />
            <Field
              label="Commercialista — Nome"
              name="accountantName"
              defaultValue={def('accountantName')}
              colSpan={2}
            />
            <Field
              label="Commercialista — Email"
              name="accountantEmail"
              defaultValue={def('accountantEmail')}
            />
            <Field
              label="Commercialista — Telefono"
              name="accountantPhone"
              defaultValue={def('accountantPhone')}
            />
            <div>
              <Label htmlFor="fatturaElettronicaEnabled">Fatturazione elettronica</Label>
              <Select
                id="fatturaElettronicaEnabled"
                name="fatturaElettronicaEnabled"
                defaultValue={def('fatturaElettronicaEnabled') || 'true'}
              >
                <option value="true">Abilitata</option>
                <option value="false">Disabilitata</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="stampDutyEnabled">Bollo virtuale</Label>
              <Select
                id="stampDutyEnabled"
                name="stampDutyEnabled"
                defaultValue={def('stampDutyEnabled') || 'false'}
              >
                <option value="true">Sì</option>
                <option value="false">No</option>
              </Select>
            </div>
          </Grid>
          <SaveButton saving={saving} />
        </form>
      </CardBody>
    </Card>
  )
}

function PricingForm({
  data,
  onSave,
  saving,
}: {
  data: FullConfig['pricing']
  onSave: (patch: Record<string, unknown>) => void
  saving: boolean
}) {
  const [services, setServices] = useState(data?.services ?? [])
  const [hourlyRate, setHourlyRate] = useState(data?.hourlyRate ?? 0)
  const [hourlyRateOnsite, setHourlyRateOnsite] = useState(data?.hourlyRateOnsite ?? 0)
  const [currency, setCurrency] = useState(data?.currency ?? 'EUR')
  const [paymentTerms, setPaymentTerms] = useState(data?.paymentTermsDefault ?? '')
  const [warrantyMonths, setWarrantyMonths] = useState(data?.warrantyMonths ?? 12)
  const [promo, setPromo] = useState(data?.promo ?? '')
  const [notes, setNotes] = useState(data?.notes ?? '')

  function addService() {
    setServices([...services, { name: '', unit: 'progetto' }])
  }
  function removeService(idx: number) {
    setServices(services.filter((_, i) => i !== idx))
  }
  function updateService(idx: number, patch: Partial<(typeof services)[number]>) {
    setServices(services.map((s, i) => (i === idx ? { ...s, ...patch } : s)))
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    onSave({
      hourlyRate: Number(hourlyRate) || undefined,
      hourlyRateOnsite: Number(hourlyRateOnsite) || undefined,
      currency,
      paymentTermsDefault: paymentTerms || undefined,
      warrantyMonths: Number(warrantyMonths) || undefined,
      promo: promo || undefined,
      notes: notes || undefined,
      services: services.filter((s) => s.name.trim()),
    })
  }

  return (
    <Card>
      <CardBody>
        <form onSubmit={onSubmit} className="space-y-5">
          <h3 className="text-base font-semibold text-zinc-50">Listino & condizioni</h3>
          <Grid>
            <div>
              <Label htmlFor="hourlyRate">Tariffa oraria (€)</Label>
              <Input
                id="hourlyRate"
                type="number"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(Number(e.currentTarget.value))}
              />
            </div>
            <div>
              <Label htmlFor="hourlyRateOnsite">Tariffa on-site (€)</Label>
              <Input
                id="hourlyRateOnsite"
                type="number"
                value={hourlyRateOnsite}
                onChange={(e) => setHourlyRateOnsite(Number(e.currentTarget.value))}
              />
            </div>
            <div>
              <Label htmlFor="currency">Valuta</Label>
              <Input
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.currentTarget.value)}
              />
            </div>
            <div>
              <Label htmlFor="warrantyMonths">Garanzia (mesi)</Label>
              <Input
                id="warrantyMonths"
                type="number"
                value={warrantyMonths}
                onChange={(e) => setWarrantyMonths(Number(e.currentTarget.value))}
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="paymentTerms">Termini di pagamento default</Label>
              <Input
                id="paymentTerms"
                value={paymentTerms}
                onChange={(e) => setPaymentTerms(e.currentTarget.value)}
                placeholder="30/30/40 — avvio, milestone, consegna"
              />
            </div>
            <div className="col-span-2">
              <Label htmlFor="promo">Promo attiva</Label>
              <Input id="promo" value={promo} onChange={(e) => setPromo(e.currentTarget.value)} />
            </div>
            <div className="col-span-2">
              <Label htmlFor="notes">Note</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.currentTarget.value)}
              />
            </div>
          </Grid>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Servizi a listino</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addService}>
                + Aggiungi servizio
              </Button>
            </div>
            <div className="space-y-2">
              {services.map((s, idx) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: editable row order is stable (append/remove via splice); items have no stable id
                  key={idx}
                  className="rounded-md border border-zinc-800 bg-zinc-950/40 p-3 grid grid-cols-12 gap-2"
                >
                  <div className="col-span-4">
                    <Input
                      placeholder="Nome servizio"
                      value={s.name}
                      onChange={(e) => updateService(idx, { name: e.currentTarget.value })}
                    />
                  </div>
                  <div className="col-span-3">
                    <Input
                      placeholder="Descrizione"
                      value={s.description ?? ''}
                      onChange={(e) => updateService(idx, { description: e.currentTarget.value })}
                    />
                  </div>
                  <div className="col-span-1">
                    <Input
                      type="number"
                      placeholder="da"
                      value={s.priceFrom ?? ''}
                      onChange={(e) =>
                        updateService(idx, {
                          priceFrom: e.currentTarget.value
                            ? Number(e.currentTarget.value)
                            : undefined,
                        })
                      }
                    />
                  </div>
                  <div className="col-span-1">
                    <Input
                      type="number"
                      placeholder="a"
                      value={s.priceTo ?? ''}
                      onChange={(e) =>
                        updateService(idx, {
                          priceTo: e.currentTarget.value
                            ? Number(e.currentTarget.value)
                            : undefined,
                        })
                      }
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      placeholder="unità"
                      value={s.unit ?? ''}
                      onChange={(e) => updateService(idx, { unit: e.currentTarget.value })}
                    />
                  </div>
                  <div className="col-span-1 flex justify-end items-center">
                    <button
                      type="button"
                      onClick={() => removeService(idx)}
                      className="text-xs text-zinc-500 hover:text-red-400"
                    >
                      ✕
                    </button>
                  </div>
                  {s.notes !== undefined && (
                    <div className="col-span-12">
                      <Input
                        placeholder="Note"
                        value={s.notes ?? ''}
                        onChange={(e) => updateService(idx, { notes: e.currentTarget.value })}
                        className="text-xs"
                      />
                    </div>
                  )}
                </div>
              ))}
              {services.length === 0 && (
                <p className="text-sm text-zinc-500">Nessun servizio. Aggiungine uno.</p>
              )}
            </div>
          </div>

          <SaveButton saving={saving} />
        </form>
      </CardBody>
    </Card>
  )
}

function BrandingForm({
  data,
  onSave,
  saving,
}: {
  data: FullConfig['branding']
  onSave: (patch: Record<string, unknown>) => void
  saving: boolean
}) {
  const [signature, setSignature] = useState(data?.emailSignatureHtml ?? '')

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const fd = new FormData(e.currentTarget)
    const patch: Record<string, unknown> = {}
    for (const [k, v] of fd.entries()) {
      const s = String(v).trim()
      patch[k] = s === '' ? undefined : s
    }
    patch.emailSignatureHtml = signature || undefined
    onSave(patch)
  }
  const def = (k: keyof FullConfig['branding']) => String(data?.[k] ?? '')

  return (
    <Card>
      <CardBody>
        <form onSubmit={onSubmit} className="space-y-4">
          <h3 className="text-base font-semibold text-zinc-50">Branding & firma email</h3>
          <Grid>
            <Field label="Colore primario" name="primaryColor" defaultValue={def('primaryColor')} />
            <Field
              label="Colore secondario"
              name="secondaryColor"
              defaultValue={def('secondaryColor')}
            />
            <Field label="Colore accent" name="accentColor" defaultValue={def('accentColor')} />
            <Field label="Logo URL" name="logoUrl" defaultValue={def('logoUrl')} />
            <Field label="Tagline" name="tagline" defaultValue={def('tagline')} colSpan={2} />
          </Grid>

          <div>
            <Label htmlFor="signature">Firma email (HTML)</Label>
            <Textarea
              id="signature"
              value={signature}
              onChange={(e) => setSignature(e.currentTarget.value)}
              className="font-mono text-xs min-h-48"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Inclusa in fondo a tutte le email inviate via Gmail MCP. La visualizzi nelle bozze
              email reali (Gmail renderizza l'HTML al momento dell'invio).
            </p>
          </div>

          <SaveButton saving={saving} />
        </form>
      </CardBody>
    </Card>
  )
}

// ============================================================
// Form helpers
// ============================================================

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
}

function Field({
  label,
  name,
  defaultValue,
  type = 'text',
  colSpan,
}: {
  label: string
  name: string
  defaultValue?: string
  type?: string
  colSpan?: 1 | 2
}) {
  return (
    <div className={cn(colSpan === 2 && 'sm:col-span-2')}>
      <Label htmlFor={name}>{label}</Label>
      <Input id={name} name={name} type={type} defaultValue={defaultValue} />
    </div>
  )
}

function TextField({
  label,
  name,
  defaultValue,
  colSpan,
}: {
  label: string
  name: string
  defaultValue?: string
  colSpan?: 1 | 2
}) {
  return (
    <div className={cn(colSpan === 2 && 'sm:col-span-2')}>
      <Label htmlFor={name}>{label}</Label>
      <Textarea id={name} name={name} defaultValue={defaultValue} />
    </div>
  )
}

function SaveButton({ saving }: { saving: boolean }) {
  return (
    <div className="flex justify-end pt-2 border-t border-zinc-800/60">
      <Button type="submit" disabled={saving}>
        <Save className="size-4" /> {saving ? 'Salvataggio…' : 'Salva'}
      </Button>
    </div>
  )
}
